<template>
  <div class="business-documents">
    <div class="business-documents__container">
      <b-overlay :show="loading" rounded="sm" class="business-documents__overlay">
        <div class="business-documents__card">
          <BrPageHeader
            :title="$t('lbl_EMPBE_BusinessDocuments')"
            :description="$t('lbl_EMPBE_BR_Page_Desc')"
            @add="goAddDocument"
          />

          <div class="br-list">
            <BrListSection
              :title="$t('lbl_EMPBE_BR_List_Section_CurrentDisplay')"
              :items="currentDisplayItems"
              key-prefix="current"
              is-current-display
              :show-empty="!hasBrApprovedDisplayDocument(documentList)"
            >
              <template #item="{ item }">
                <BrDocumentListItem
                  v-bind="documentItemProps(item, true)"
                  v-on="documentItemListeners"
                />
              </template>
              <template #empty>
                <BrEmptyBanner />
              </template>
            </BrListSection>

            <BrListSection
              v-if="recordItems.length"
              :title="$t('lbl_EMPBE_BR_List_Section_Records')"
              :items="recordItems"
              key-prefix="record"
            >
              <template #item="{ item }">
                <BrDocumentListItem
                  v-bind="documentItemProps(item, false)"
                  v-on="documentItemListeners"
                />
              </template>
            </BrListSection>
          </div>
        </div>
      </b-overlay>
    </div>

    <BrImagePreviewModal
      v-model="showPreview"
      :src="previewSrc"
      :item="previewItem"
      appearance="supplier"
    />

    <BrContactUsDrawer ref="contactUsDrawer" />

    <BrNameChangeModal
      :visible="showNameChangeModal"
      :document-list="documentList"
      :source-approval-id="nameChangeSourceApprovalId"
      :current-display-name-en="companyNameEn"
      :current-display-name-local="companyNameLocal"
      @close="closeNameChangeModal"
      @submitted="handleNameChangeSubmitted"
    />

    <BrConfirmModal
      :visible="showSetAsCurrentModal"
      :title="$t('lbl_EMPBE_BR_SetAsCurrent_ConfirmTitle')"
      :body="$t('lbl_EMPBE_BR_SetAsCurrent_ConfirmBody')"
      :cancel-label="$t('lbl_EMPBE_BR_SetAsCurrent_Cancel')"
      :confirm-label="$t('lbl_EMPBE_BR_SetAsCurrent_Update')"
      :submitting="setAsCurrentSubmitting"
      @cancel="closeSetAsCurrentModal"
      @confirm="confirmSetAsCurrent"
    />

    <BrConfirmModal
      :visible="showSetAsCurrentSuccess"
      :title="$t('lbl_EMPBE_BR_SetAsCurrent_SuccessTitle')"
      :body="$t('lbl_EMPBE_BR_SetAsCurrent_SuccessBody')"
      :confirm-label="$t('lbl_EMPBE_BR_SetAsCurrent_Ok')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="showSetAsCurrentSuccess = false"
      @confirm="showSetAsCurrentSuccess = false"
    />

    <BrConfirmModal
      :visible="showNameChangeSuccess"
      :title="nameChangeSuccessTitle"
      :body="nameChangeSuccessBody"
      :confirm-label="$t('lbl_EMPBE_BR_NameChange_SuccessOk')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="closeNameChangeSuccess"
      @confirm="closeNameChangeSuccess"
    />

    <BrConfirmModal
      :visible="showCancelConfirm"
      :title="$t('lbl_EMPBE_BR_CancelConfirm_Title')"
      :body="$t('lbl_EMPBE_BR_CancelConfirm_Body')"
      :cancel-label="$t('lbl_EMPBE_BR_CancelConfirm_Keep')"
      :confirm-label="$t('lbl_EMPBE_BR_CancelConfirm_Ok')"
      :submitting="cancelProcessingId != null"
      @cancel="closeCancelConfirm"
      @confirm="confirmCancelSubmission"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BrDocumentListItem from './components/BrDocumentListItem.vue';
import BrListSection from './components/BrListSection.vue';
import BrContactUsDrawer from './components/BrContactUsDrawer.vue';
import BrNameChangeModal from './components/BrNameChangeModal.vue';
import BrConfirmModal from './components/BrConfirmModal.vue';
import BrEmptyBanner from './components/BrEmptyBanner.vue';
import BrImagePreviewModal from './components/BrImagePreviewModal.vue';
import BrPageHeader from './components/BrPageHeader.vue';
import brSetAsCurrent from './mixins/brSetAsCurrent';
import brNameChangeFlow from './mixins/brNameChangeFlow';
import brImagePreview from './mixins/brImagePreview';
import brCancelRejected from './mixins/brCancelRejected';
import {
  loadBrSubmissionStatus,
  brRuntimeState,
  syncAuthCompanyNames,
  invalidateBrRuntimeCache,
  consumeBrRuntimeLoadError,
} from '../../constants/businessRegistration';
import {
  pickBrDisplaySections,
  hasBrApprovedDisplayDocument,
} from './utils/brDocument';
import {
  buildBusinessDocumentsListSections,
  getApprovedBrForNameChangeOptions,
} from './utils/brNameChange';
import {
  resolveBrCompanyNameEn,
  resolveBrCompanyNameZh,
} from './utils/brCompanyNames';

export default {
  name: 'BusinessDocumentsDefault',
  mixins: [brSetAsCurrent, brNameChangeFlow, brImagePreview, brCancelRejected],
  components: {
    BrDocumentListItem,
    BrListSection,
    BrContactUsDrawer,
    BrNameChangeModal,
    BrConfirmModal,
    BrEmptyBanner,
    BrImagePreviewModal,
    BrPageHeader,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapState('authentication', ['user']),
    summary() {
      return brRuntimeState.summary;
    },
    documentList() {
      return brRuntimeState.documentList || [];
    },
    langid() {
      return localStorage.getItem('currentLangId');
    },
    companyNameEn() {
      return resolveBrCompanyNameEn({
        summary: this.summary,
        user: this.user,
        emptyFallback: '—',
      });
    },
    companyNameLocal() {
      return resolveBrCompanyNameZh({
        summary: this.summary,
        user: this.user,
        langId: this.langid,
      });
    },
    nameChangePendingItem() {
      return brRuntimeState.nameChangePending;
    },
    pageListSections() {
      return buildBusinessDocumentsListSections(
        this.documentList,
        this.nameChangePendingItem,
        (key) => this.$t(key),
      );
    },
    displaySections() {
      return pickBrDisplaySections(this.pageListSections);
    },
    currentDisplayItems() {
      return this.displaySections.currentDisplayItems;
    },
    recordItems() {
      return this.displaySections.recordItems;
    },
    approvedBrForNameChange() {
      return getApprovedBrForNameChangeOptions(this.documentList);
    },
    /** 有生效 BR 即可申請更名（不因待審鎖死） */
    canRequestNameChange() {
      return this.approvedBrForNameChange.length > 0;
    },
    documentItemListeners() {
      return {
        preview: this.openBrImagePreview,
        reupload: this.goAddDocument,
        cancel: this.handleCancel,
        contact: this.openContactUs,
        'edit-name': this.openNameChangeModal,
        'set-as-current': this.openSetAsCurrentModal,
      };
    },
  },
  mounted() {
    this.loadPageData();
  },
  methods: {
    hasBrApprovedDisplayDocument,
    documentItemProps(item, isCurrentDisplay) {
      return {
        item,
        displayNameEn: this.companyNameEn,
        displayNameLocal: this.companyNameLocal,
        thumbUrl: this.resolveBrThumbUrl(item),
        isCurrentDisplay,
        canEditName: this.canRequestNameChange,
      };
    },
    async loadPageData() {
      // 进列表页始终硬刷：驳回原因等管理端刚改的字段不能吃登录缓存
      this.loading = true;
      try {
        await loadBrSubmissionStatus('hard');
        const loadError = consumeBrRuntimeLoadError();
        if (loadError === 'SUMMARY' || loadError === 'LIST' || loadError === 'PARTIAL') {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_List_LoadFailed'));
        } else if (loadError === 'NAME_CHANGE') {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_NameChange_PendingLoadFailed'));
        }
        const summary = brRuntimeState.summary || {};
        if ('currentNameEn' in summary || 'CurrentNameEn' in summary
          || 'currentNameZh' in summary || 'CurrentNameZh' in summary) {
          syncAuthCompanyNames({
            companyNameEn: summary.currentNameEn ?? summary.CurrentNameEn ?? '',
            companyNameZh: summary.currentNameZh ?? summary.CurrentNameZh ?? '',
          });
        }
      } finally {
        this.loading = false;
      }
    },
    goAddDocument() {
      this.$router.push('/businessDocuments/addDocument');
    },
    openContactUs() {
      this.$refs.contactUsDrawer?.open();
    },
    async afterCancelRejectedSuccess() {
      invalidateBrRuntimeCache();
      await loadBrSubmissionStatus('hard');
      this.$toast?.success?.(this.$t('lbl_EMPBE_BR_Cancel_Success'));
    },
    getNameChangeDocumentList() {
      return this.documentList;
    },
    async afterNameChangeSubmitted() {
      // 提交仅建 PA：展台名保持旧名，待管理员通过后再由 GetSummary / Company 刷新
      await loadBrSubmissionStatus('hard');
    },
    async afterSetAsCurrentSuccess({ nameEn, nameZh }) {
      if (nameEn || nameZh) {
        syncAuthCompanyNames({ companyNameEn: nameEn, companyNameZh: nameZh });
      }
      await loadBrSubmissionStatus('hard');
    },
  },
};
</script>

<style lang="scss">
@import './styles/br-shared.scss';
</style>
